import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import { formatDateTime } from "../../../utils/dateTimeFormat";
import { Card, CardContent, Typography, Box, CircularProgress, Chip, IconButton, Stack } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DotMenu from "../../DotMenu";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const JobCard = ({ job, children }) => {
  return (
    <Card sx={{ mb: 1 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {job.jobName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Translate.get("QueuedAt")}: {formatDateTime(job.queuedAtUtc)}
            </Typography>
            {job.queuedBy && (
              <Typography variant="body2" color="text.secondary">
                {Translate.get("QueuedBy")}: {job.queuedBy}
              </Typography>
            )}
            {job.scheduledRunTimeUtc && (
              <Typography variant="body2" color="text.secondary">
                {Translate.get("Scheduled")}: {formatDateTime(job.scheduledRunTimeUtc)}
              </Typography>
            )}
            {job.parameters && (
              <Typography variant="body2" color="text.secondary">
                {Translate.get("Parameters")}:
                <ul>
                  {Object.entries(job.parameters).map(([k, v]) => (
                    <li>{`${k}: ${v}`}</li>
                  ))}
                </ul>
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {job.isRecurring && <Chip label={Translate.get("Recurring")} size="small" />}
            </Stack>
          </Box>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function JobQueueView({ queueData, reloadQueueData, loadingQueueData }) {
  const api = useApi();

  useEffect(() => {
    reloadQueueData();
  }, [reloadQueueData]);

  async function handleRemove(index) {
    await api.fetch(`${import.meta.env.VITE_BGJOBS_URL}bgjobs/queue/${index}`, true, "DELETE");
    await reloadQueueData();
  }

  async function handleMove(fromIndex, toIndex) {
    await api.fetch(`${import.meta.env.VITE_BGJOBS_URL}bgjobs/queue/reorder`, { fromIndex, toIndex }, "PUT");
    await reloadQueueData();
  }

  function getDotMenuContent(job, index, totalPending) {
    return [
      {
        icon: <DeleteIcon />,
        text: Translate.get("Delete"),
        onClick: () => handleRemove(index),
        disabled: job.isRecurring,
      },
      {
        icon: <ArrowUpwardIcon />,
        text: Translate.get("MoveUp"),
        onClick: () => handleMove(index, index - 1),
        disabled: job.isRecurring || index === 0,
      },
      {
        icon: <ArrowDownwardIcon />,
        text: Translate.get("MoveDown"),
        onClick: () => handleMove(index, index + 1),
        disabled: job.isRecurring || index === totalPending - 1,
      },
    ];
  }

  if (loadingQueueData && !queueData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentlyRunning = queueData?.currentlyRunning;
  const pendingJobs = queueData?.pendingJobs ?? [];
  console.log("currentlyRunning", currentlyRunning);
  console.log("pendingJobs", pendingJobs);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">{Translate.get("CurrentlyRunning")}</Typography>
        <IconButton onClick={reloadQueueData} title={Translate.get("Refresh")}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {currentlyRunning ? (
        <Card
          sx={{
            mb: 3,
            border: "1px solid",
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          }}
        >
          <CardContent sx={{ "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CircularProgress size={24} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {currentlyRunning.jobName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Translate.get("QueuedAt")}: {formatDateTime(currentlyRunning.queuedAtUtc)}
                </Typography>
                {currentlyRunning.queuedBy && (
                  <Typography variant="body2" color="text.secondary">
                    {Translate.get("QueuedBy")}: {currentlyRunning.queuedBy}
                  </Typography>
                )}
                {currentlyRunning.parameters && (
                  <Typography variant="body2" color="text.secondary">
                    {Translate.get("Parameters")}:
                    <ul>
                      {Object.entries(currentlyRunning.parameters).map(([k, v]) => (
                        <li>{`${k}: ${v}`}</li>
                      ))}
                    </ul>
                  </Typography>
                )}
                {currentlyRunning.isRecurring && (
                  <Chip label={Translate.get("Recurring")} size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {Translate.get("NoJobRunning")}
        </Typography>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        {Translate.get("PendingJobs")}
      </Typography>

      {pendingJobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {Translate.get("NoJobsInQueue")}
        </Typography>
      ) : (
        pendingJobs.map((job, index) => (
          <JobCard key={index} job={job}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip label={`${Translate.get("Position")} ${index + 1}`} size="small" variant="outlined" />
              <DotMenu content={getDotMenuContent(job, index, pendingJobs.length)} />
            </Stack>
          </JobCard>
        ))
      )}
    </Box>
  );
}
